import { Entity, ProppingSystem, Vector } from '@inter-ikea-core/core';
import { Dimensions, Product } from '@inter-ikea-storageone/range';
import { useLayoutEffect, useRef } from 'react';

import styled from 'styled-components';
import { useCore } from 'contexts/core';
import { addInteriorSuggestionEntityToFrame } from 'rangeConfig/shared/utils/interiorSuggestions';
import { reportAddMultiFrameInteriorCombination } from 'analytics/reporters';
import ProductCard from 'components/ProductSidebar/ProductCard/ProductCard';
import { ListItem } from 'components/ProductSidebar/menus/menuComponents';
import { isMobile } from 'utils/platform';
import { useSelector } from 'react-redux';
import { isProppingVisible } from 'ducks/layout';

export type FrameAndInteriorSuggestions = {
  frame: Entity;
  suggestion: Product;
  dimensions: Dimensions;
  worldPosition: Vector;
};

interface IInteriorSuggestionMultipleFrame {
  frames: FrameAndInteriorSuggestions[];
  menu: string;
  wideCards: boolean;
}

const CanvasWrapper = styled.div`
  display: flex;
`;

const StyledCanvas = styled.canvas`
  margin: 0 auto;
  cursor: pointer;
  padding: 4px;
  background: #fff;
`;

const InteriorSuggestionMultipleFrame = ({
  frames,
  menu,
  wideCards
}: IInteriorSuggestionMultipleFrame) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const coreApi = useCore();
  const proppingVisible = useSelector(isProppingVisible);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;

    if (canvas) {
      const sizeScale = wideCards ? 2 : 1.5;
      canvas.width =
        (frames.reduce((a, b) => {
          return a + b.dimensions.width;
        }, 0) /
          10) *
        sizeScale;
      canvas.height = (frames[0].suggestion.communicatedDimensions.height / 10) * sizeScale;

      const context = canvas.getContext('2d');

      if (context) {
        frames.forEach((frame, i) => {
          const frameWidth = (frame.dimensions.width / 10) * sizeScale;
          const frameXPosition = frames.slice(0, i).reduce((a, b) => {
            return a + b.dimensions.width;
          }, 0);
          const image = new Image();

          image.src = frame.suggestion.images[0].sources[0].src;
          if (image) {
            // Apply image to canvas when it has been loaded
            image.onload = () => {
              const suggestionHeight =
                (frame.suggestion.communicatedDimensions.height / 10) * sizeScale;
              context.drawImage(
                image,
                (frameXPosition / 10) * sizeScale,
                0,
                frameWidth,
                suggestionHeight
              );
            };
          }
        });
      }
    }
  });

  const handleClick = () => {
    return coreApi.history.historyAction(async () => {
      if (frames.length > 0) {
        await coreApi.performLongCoreOperation(() => {
          proppingVisible && coreApi.getSystem<ProppingSystem>('ProppingSystem')?.disable();
          frames.forEach(frame => {
            const interiorSuggestionEntity = coreApi
              .getServices()
              .productManager.createEntity(frame.suggestion.coreProductId);
            if (!interiorSuggestionEntity) return;
            addInteriorSuggestionEntityToFrame(interiorSuggestionEntity, frame.frame, coreApi);
          });
          proppingVisible && coreApi.getSystem<ProppingSystem>('ProppingSystem')?.enable();
        });
        sendToAnalytics(frames, menu);
      }
    });
  };

  if (frames.length === 0) return <></>;
  return (
    <ListItem
      style={{ maxWidth: wideCards ? '100%' : '', display: 'flex', justifyContent: 'center' }}
      data-testid={frames.map(f => f.suggestion.coreProductId)}
    >
      <ProductCard
        product={{
          ...frames[0].suggestion,
          images: [],
          parts: frames.flatMap(f => f.suggestion.parts),
          coreProductId: frames.map(f => f.suggestion.coreProductId).toString()
        }}
        handleClick={handleClick}
        infoButtonAriaLabel={'storageone.selectedMenuInteriorsCombinationsMenuTitle'}
        showInfoBtn={false}
        showPrice={!isMobile()}
        showAvailability={!isMobile()}
        children={
          <CanvasWrapper>
            <StyledCanvas
              ref={canvasRef}
              style={{
                maxHeight: isMobile() ? (wideCards ? '200px' : '90px') : 'auto',
                width: isMobile() ? 'auto' : '100%'
              }}
            />
          </CanvasWrapper>
        }
      />
    </ListItem>
  );
};

export default InteriorSuggestionMultipleFrame;

const sendToAnalytics = (frames: FrameAndInteriorSuggestions[], menu: string) => {
  const combinations = frames.map(frameInteriorSuggestion => ({
    articles: frameInteriorSuggestion.suggestion.parts.map(part => `${part.itemType}-${part.id}`),
    assemblyId: frameInteriorSuggestion.suggestion.coreProductId,
    menu: menu
  }));

  reportAddMultiFrameInteriorCombination({ combinations });
};
